import { FMDPStorage } from "./helper";

export class CurrentUser {
  constructor(resourceName) {
    const { name, app_features, email_id } = FMDPStorage.get("current_user") || {};
    this.appFeatures = FMDPStorage.get("app_features");
    this.pluginPermissions = FMDPStorage.get("plugin_features");
    this.name = name;
    this.email = email_id;
    this.resourceName = resourceName;
    this.canCreate = this.can("create");
    this.canDelete = this.can("delete");
    this.canUpdate = this.can("update");
    this.canRead = this.can("read");
  }

  can = (action) => {
    return this.appFeatures[this.resourceName]?.[action];
  };

  /**
   * Check permission for a specific plugin sub-feature action.
   * Checks Source 1 (app_features with "::" separator) first,
   * then falls back to Source 2 (plugin_feature_permissions from localStorage).
   * @param {string} pluginName - Plugin name
   * @param {string} featureKey - Feature key within the plugin
   * @param {string} action - CRUD action ("read", "create", "update", "delete")
   * @returns {boolean} true if the action is allowed in either source
   */
  canPluginFeature = (pluginName, featureKey, action) => {
    // Source 1: Check app_features with "::" format
    const appFeatureResult = !!this.appFeatures?.[`${pluginName}::${featureKey}`]?.[action];
    if (appFeatureResult) return true;

    // Source 2: Check plugin_feature_permissions
    if (Array.isArray(this.pluginPermissions)) {
      for (const pfp of this.pluginPermissions) {
        const permJson = pfp.permission_json;
        if (permJson?.[featureKey]?.[action]) return true;
      }
    }

    return false;
  };

  /**
   * Check if user has read access to at least one sub-feature of a plugin.
   * Checks Source 1 (app_features with "::" prefix) first,
   * then falls back to Source 2 (plugin_feature_permissions from localStorage).
   * @param {string} pluginName - Plugin name
   * @returns {boolean} true if any sub-feature has read permission in either source
   */
  hasAnyPluginAccess = (pluginName) => {
    // Source 1: Check app_features keys starting with "pluginName::"
    const prefix = `${pluginName}::`;
    const hasAppFeatureAccess = Object.keys(this.appFeatures || {}).some(
      (key) => key.startsWith(prefix) && !!this.appFeatures[key]?.read
    );
    if (hasAppFeatureAccess) return true;

    // Source 2: Check plugin_feature_permissions
    if (Array.isArray(this.pluginPermissions)) {
      return this.pluginPermissions.some((pfp) => {
        const permJson = pfp.permission_json;
        return permJson && Object.values(permJson).some((perms) => !!perms?.read);
      });
    }

    return false;
  };
}
